import React, { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Search,
  Menu,
  X,
  ChevronDown,
  ArrowRight,
  MessageSquare
} from 'lucide-react';
import { useIntl } from 'react-intl';
import { logo } from '../../assets';
import { fetchDomains } from '../../api/strapi/domains';
import { transformDomainsToMegaMenuStructure } from '../../api/strapi/utils/domains-transformer';
import MegaMenu from './mega-menu/MegaMenu';
import MobileMegaMenu from './mega-menu/MobileMegaMenu';
import SearchModal from './SearchModal';
import { useLanguage } from '../../context';
import { getLocalizedPath } from '../../helpers/routes';

const MainNav = ({ onMegaMenuChange, globalData, isScrolled = false, topBarHidden = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState(null);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timeoutRef = useRef(null);

  const intl = useIntl();
  const { language } = useLanguage();

  const getCategoryLabel = (category) => {
    if (!category) return '';
    if (language !== 'fr') {
      return category.title;
    }

    const fallbackMap = {
      products: intl.formatMessage({ id: 'menu.products' }),
      industries: intl.formatMessage({ id: 'menu.industries' }),
      services: intl.formatMessage({ id: 'menu.services' }),
      rds: intl.formatMessage({ id: 'menu.rd' }),
      companies: intl.formatMessage({ id: 'menu.companies' })
    };

    return fallbackMap[category.slug] || category.title;
  };

  const getCategoryKey = (category) => category?.slug || category?.id || category?.title;

  // Charger les données des domaines depuis Strapi
  useEffect(() => {
    const loadDomains = async () => {
      try {
        setLoading(true);
        const result = await fetchDomains();
        if (!result.ok) {
          if (result.reason !== "NOT_FOUND") {
            console.warn("Domains unavailable:", result);
            setError("Menu temporarily unavailable");
          }
          setDomains([]);
          return;
        }

        const transformedDomains = transformDomainsToMegaMenuStructure(result.data);
        setDomains(transformedDomains);
      } catch (err) {
        console.error("Erreur lors du chargement des domaines:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadDomains();
  }, []);

  // Fonction pour gérer l'ouverture du mega menu
  const openMegaMenu = (menuKey) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setActiveMegaMenu(menuKey);
    // Notifier le parent que le mega menu s'ouvre
    if (onMegaMenuChange) {
      onMegaMenuChange(true);
    }
  };

  // Fonction pour fermer le mega menu
  const closeMegaMenu = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveMegaMenu(null);
      // Notifier le parent que le mega menu se ferme
      if (onMegaMenuChange) {
        onMegaMenuChange(false);
      }
    }, 200);
  };

  // Fonction pour garder le mega menu ouvert
  const keepMegaMenuOpen = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  // Fonction pour basculer le modal de recherche
  const toggleSearchModal = () => {
    setShowSearchModal(!showSearchModal);
  };

  // Fonction pour fermer le modal de recherche
  const closeSearchModal = () => {
    setShowSearchModal(false);
  };

  // Nettoyer les timeouts
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Afficher un loader si les données sont en cours de chargement
  if (loading) {
    return (
      <header className="bg-white w-full border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <a href='/'>
                <img
                  src={logo}
                  alt="AFS Logo"
                  className="h-12 w-auto"
                />
              </a>
            </div>
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#16A34A]"></div>
            </div>
          </div>
        </div>
      </header>
    );
  }

  // Afficher une erreur si le chargement a échoué
  if (error) {
    console.error("Erreur dans MainNav:", error);
    return (
      <header className="bg-white w-full border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <a href='/'>
                <img
                  src={logo}
                  alt="AFS Logo"
                  className="h-12 w-auto"
                />
              </a>
            </div>
            <div className="text-gray-500 text-sm">{intl.formatMessage({ id: "menu.unavailable" })}</div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <>
      {/* Search Modal - Global Search Component */}
      <SearchModal isOpen={showSearchModal} onClose={closeSearchModal} />

      <header className={`bg-white w-full border-b transition-shadow duration-300 ${isScrolled ? 'border-slate-200 shadow-sm' : 'border-slate-200'}`}>
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <a href='/'>
                {globalData?.logo ? (
                  <img
                    src={`${process.env.REACT_APP_STRAPI_URL}${globalData.logo.url}`}
                    alt={globalData.logo.alternativeText || "AFS Logo"}
                    className="h-12 w-auto"
                  />
                ) : (
                  <img
                    src={logo}
                    alt="AFS Logo"
                    className="h-12 w-auto"
                  />
                )}
              </a>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {domains.map((category) => {
                const categoryKey = getCategoryKey(category);
                const isAboutUs = category.type === 'companies';

                if (isAboutUs) {
                  return (
                    <NavLink
                      key={category.id}
                      to={getLocalizedPath('/info/company/company', language)}
                      className={({ isActive }) =>
                        `relative px-3 py-2 text-sm font-semibold uppercase tracking-wide transition-colors duration-200 after:absolute after:left-3 after:right-3 after:-bottom-0.5 after:h-[2px] after:bg-[#16A34A] after:transition-transform after:duration-200 after:origin-left ${
                          isActive
                            ? 'text-[#16A34A] after:scale-x-100'
                            : 'text-[#0a1628] hover:text-[#16A34A] after:scale-x-0 hover:after:scale-x-100'
                        }`
                      }
                    >
                      {getCategoryLabel(category)}
                    </NavLink>
                  );
                }

                return (
                  <div
                    key={category.id}
                    className="relative group"
                    onMouseEnter={() => openMegaMenu(categoryKey)}
                    onMouseLeave={closeMegaMenu}
                  >
                    <button
                      className={`relative flex items-center space-x-1 px-3 py-2 text-sm font-semibold uppercase tracking-wide transition-colors duration-200 after:absolute after:left-3 after:right-3 after:-bottom-0.5 after:h-[2px] after:bg-[#16A34A] after:transition-transform after:duration-200 after:origin-left ${
                        activeMegaMenu === categoryKey
                          ? 'text-[#16A34A] after:scale-x-100'
                          : 'text-[#0a1628] hover:text-[#16A34A] after:scale-x-0 hover:after:scale-x-100'
                      }`}
                    >
                      <span>{getCategoryLabel(category)}</span>
                      <ChevronDown
                        className={`transition-transform duration-200 ${
                          activeMegaMenu === categoryKey ? 'rotate-180' : ''
                        }`}
                        size={16}
                      />
                    </button>

                    {/* Mega Menu Dropdown */}
                    {activeMegaMenu === categoryKey && (
                      <div
                        className={`fixed left-0 w-screen bg-white shadow-xl z-[9999] border-t border-slate-200 animate-fadeIn overflow-y-auto overscroll-contain ${topBarHidden ? 'top-[64px] max-h-[calc(100vh-64px)]' : 'top-[100px] max-h-[calc(100vh-100px)]'}`}
                        onMouseEnter={keepMegaMenuOpen}
                        onMouseLeave={closeMegaMenu}
                      >
                        <MegaMenu
                          category={category}
                          onClose={closeMegaMenu}
                          onDomainChange={(domain) => {
                            // Gérer les changements de domaine si nécessaire
                          }}
                        />
                        <MobileMegaMenu
                          category={category}
                          onClose={closeMegaMenu}
                          onDomainChange={(domain) => {
                            // Gérer les changements de domaine si nécessaire
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* Right side buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={toggleSearchModal}
                className="p-2 text-slate-600 hover:text-[#16A34A] transition-colors duration-200"
              >
                <Search size={20} />
              </button>

              <a
                href={getLocalizedPath('/contact', language)}
                className="bg-[#16A34A] hover:bg-[#15803D] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-200 inline-flex items-center gap-2"
              >
                <MessageSquare size={18} />
                <span>{intl.formatMessage({ id: "nav.contactButton" })}</span>
              </a>

              {/* Mobile menu button */}
              <button
                className="lg:hidden p-2 text-slate-600 hover:text-[#16A34A] transition-colors duration-200"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {isMenuOpen && (
            <div className={`lg:hidden py-4 border-t border-slate-200 overflow-y-auto overscroll-contain ${topBarHidden ? 'max-h-[calc(100vh-64px)]' : 'max-h-[calc(100vh-100px)]'}`}>
              <div className="flex flex-col space-y-4">
                {domains.map((category) => {
                  const categoryKey = getCategoryKey(category);
                  const isAboutUs = category.type === 'companies';

                  if (isAboutUs) {
                    return (
                      <div key={category.id}>
                        <NavLink
                          to={getLocalizedPath('/info/company/company', language)}
                          onClick={() => setIsMenuOpen(false)}
                          className={({ isActive }) =>
                            `flex items-center justify-between w-full text-left py-3 font-semibold uppercase ${
                              isActive ? 'text-[#16A34A]' : 'text-gray-800'
                            }`
                          }
                        >
                          <span>{getCategoryLabel(category)}</span>
                        </NavLink>
                      </div>
                    );
                  }

                  return (
                    <div key={category.id}>
                      <button
                        onClick={() =>
                          setActiveMegaMenu(
                            activeMegaMenu === categoryKey ? null : categoryKey
                          )
                        }
                        className="flex items-center justify-between w-full text-left py-3 text-gray-800 font-semibold uppercase"
                      >
                        <span>{getCategoryLabel(category)}</span>
                        <ChevronDown
                          className={`transition-transform duration-200 ${
                            activeMegaMenu === categoryKey ? 'rotate-180' : ''
                          }`}
                          size={20}
                        />
                      </button>

                      {activeMegaMenu === categoryKey && (
                        <div className="mt-4">
                          <MobileMegaMenu
                            category={category}
                            showBottomActions={false}
                            onClose={() => {
                              setActiveMegaMenu(null);
                              setIsMenuOpen(false);
                            }}
                            onDomainChange={() => {
                              // noop
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}

                <button
                  onClick={toggleSearchModal}
                  className="w-full flex items-center justify-center gap-2 py-3 text-slate-700 hover:text-[#16A34A] transition-colors"
                >
                  <Search size={18} />
                <span>{intl.formatMessage({ id: "nav.search" })}</span>
                </button>

                <a
                  href={getLocalizedPath('/contact', language)}
                  className="w-full bg-[#16A34A] hover:bg-[#15803D] text-white px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] flex items-center justify-center gap-2 transition-colors"
                >
                  <MessageSquare size={18} />
                  <span>{intl.formatMessage({ id: "nav.contactButton" })}</span>
                </a>
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default MainNav;
